import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Trash2, Bell, Mail, MessageSquare, Clock, AlertCircle, Code, ChevronDown, FileText, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  FormTemplate,
  FormTrigger,
  getFormTriggers,
  createFormTrigger,
  updateFormTrigger,
  deleteFormTrigger,
  formatTriggerTime,
} from '../../utils/formsApi';
import { getTemplates, NotificationTemplate, NotificationChannel } from '../../utils/notificationsBroadcastApi';

interface FormTriggerModalProps {
  template: FormTemplate;
  onClose: () => void;
}

type TimeUnit = 'minutes' | 'hours' | 'days';
type DeliveryMethod = 'sms' | 'email_plain' | 'email_html';

// Convert frontend delivery method to backend API format
// EMAIL = plain text email, EMAIL_HTML = rich HTML template, SMS = text message
const toBackendDeliveryMethod = (method: DeliveryMethod): 'EMAIL' | 'EMAIL_HTML' | 'SMS' => {
  switch (method) {
    case 'sms': return 'SMS';
    case 'email_plain': return 'EMAIL';
    case 'email_html': return 'EMAIL_HTML';
    default: return 'SMS';
  }
};

interface TriggerForm {
  value: string; // Use string to allow empty input during editing
  unit: TimeUnit;
  delivery_method: DeliveryMethod;
  is_enabled: boolean;
  template_id: string;
}

const FormTriggerModal: React.FC<FormTriggerModalProps> = ({ template, onClose }) => {
  const navigate = useNavigate();
  const [triggers, setTriggers] = useState<FormTrigger[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTrigger, setEditingTrigger] = useState<FormTrigger | null>(null);
  
  // Templates state
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
  const templateDropdownRef = useRef<HTMLDivElement>(null);
  
  const [form, setForm] = useState<TriggerForm>({
    value: '3',
    unit: 'days',
    delivery_method: 'sms',
    is_enabled: true,
    template_id: '',
  });

  // Get templates filtered by current delivery method
  const filteredTemplates = templates.filter(t => t.channel === form.delivery_method);

  useEffect(() => {
    fetchTriggers();
    fetchTemplates();
  }, [template.id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target as Node)) {
        setTemplateDropdownOpen(false);
      }
    };
    if (templateDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [templateDropdownOpen]);

  const fetchTemplates = async () => {
    try {
      const templatesData = await getTemplates();
      setTemplates(templatesData);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchTriggers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getFormTriggers(template.id);
      setTriggers(data);
    } catch (err) {
      setError('Failed to load triggers');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const convertToMinutes = (value: number, unit: TimeUnit): number => {
    switch (unit) {
      case 'days': return value * 1440;
      case 'hours': return value * 60;
      case 'minutes': return value;
    }
  };

  const convertFromMinutes = (minutes: number): { value: number; unit: TimeUnit } => {
    if (minutes >= 1440 && minutes % 1440 === 0) {
      return { value: minutes / 1440, unit: 'days' };
    } else if (minutes >= 60 && minutes % 60 === 0) {
      return { value: minutes / 60, unit: 'hours' };
    }
    return { value: minutes, unit: 'minutes' };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate and parse the value
    const numericValue = parseInt(form.value) || 1;
    if (numericValue < 1) {
      setError('Time value must be at least 1');
      return;
    }
    
    setSaving(true);
    setError(null);

    try {
      const minutes_before = convertToMinutes(numericValue, form.unit);
      
      if (editingTrigger) {
        await updateFormTrigger(editingTrigger.id, {
          minutes_before,
          delivery_method: toBackendDeliveryMethod(form.delivery_method),
          is_enabled: form.is_enabled,
          template_id: form.template_id || undefined,
        });
      } else {
        await createFormTrigger(template.id, {
          minutes_before,
          delivery_method: toBackendDeliveryMethod(form.delivery_method),
          is_enabled: form.is_enabled,
          template_id: form.template_id || undefined,
        });
      }
      
      await fetchTriggers();
      resetForm();
    } catch (err) {
      setError(editingTrigger ? 'Failed to update trigger' : 'Failed to create trigger');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (triggerId: string) => {
    if (!window.confirm('Are you sure you want to delete this trigger?')) return;
    
    try {
      await deleteFormTrigger(triggerId);
      await fetchTriggers();
    } catch (err) {
      setError('Failed to delete trigger');
      console.error(err);
    }
  };

  const handleToggleEnabled = async (trigger: FormTrigger) => {
    try {
      await updateFormTrigger(trigger.id, { is_enabled: !trigger.is_enabled });
      await fetchTriggers();
    } catch (err) {
      setError('Failed to update trigger');
      console.error(err);
    }
  };

  const startEditing = (trigger: FormTrigger) => {
    const { value, unit } = convertFromMinutes(trigger.minutes_before);
    setForm({
      value: String(value),
      unit,
      delivery_method: trigger.delivery_method as DeliveryMethod,
      is_enabled: trigger.is_enabled,
      template_id: trigger.template_id || '',
    });
    setEditingTrigger(trigger);
    setShowAddForm(true);
  };

  const resetForm = () => {
    setForm({
      value: '3',
      unit: 'days',
      delivery_method: 'sms',
      is_enabled: true,
      template_id: '',
    });
    setEditingTrigger(null);
    setShowAddForm(false);
    setTemplateDropdownOpen(false);
  };

  const getDeliveryIcon = (method: string) => {
    switch (method) {
      case 'sms': return <MessageSquare className="w-4 h-4" />;
      case 'email_plain': return <Mail className="w-4 h-4" />;
      case 'email_html': return <Code className="w-4 h-4" />;
      default: return <Mail className="w-4 h-4" />;
    }
  };

  const getDeliveryLabel = (method: string) => {
    switch (method) {
      case 'sms': return 'SMS';
      case 'email_plain': return 'Plain Email';
      case 'email_html': return 'HTML Email';
      default: return method;
    }
  };

  const getDeliveryColor = (method: string) => {
    switch (method) {
      case 'sms': return 'text-green-600 dark:text-green-400';
      case 'email_plain': return 'text-orange-600 dark:text-orange-400';
      case 'email_html': return 'text-blue-600 dark:text-blue-400';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-dark-bg-secondary max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Bell className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">Form Triggers</h2>
              <p className="text-sm text-gray-500 dark:text-dark-text-muted">{template.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Info */}
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 text-sm text-blue-800 dark:text-blue-300">
            <p>
              <strong>Form Triggers</strong> automatically send email or SMS notifications to pet owners 
              with a link to complete this form before their appointment.
            </p>
          </div>

          {/* Triggers List */}
          {loading ? (
            <div className="py-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-500 dark:text-dark-text-muted mt-4">Loading triggers...</p>
            </div>
          ) : triggers.length === 0 && !showAddForm ? (
            <div className="py-8 text-center">
              <Clock className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary mb-2">No triggers configured</h3>
              <p className="text-gray-500 dark:text-dark-text-muted mb-4">
                Add a trigger to automatically notify pet owners to complete this form.
              </p>
              <button
                onClick={() => setShowAddForm(true)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Add Trigger
              </button>
            </div>
          ) : (
            <>
              {/* Existing triggers */}
              {triggers.length > 0 && (
                <div className="space-y-3 mb-6">
                  {triggers.map((trigger) => (
                    <div
                      key={trigger.id}
                      className={`flex items-center justify-between p-4 border ${
                        trigger.is_enabled 
                          ? 'border-gray-200 dark:border-dark-border bg-white dark:bg-dark-bg-secondary' 
                          : 'border-gray-100 dark:border-dark-border bg-gray-50 dark:bg-dark-bg-tertiary'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${trigger.is_enabled ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-100 dark:bg-dark-bg-tertiary'}`}>
                          {getDeliveryIcon(trigger.delivery_method)}
                        </div>
                        <div>
                          <p className={`font-medium ${trigger.is_enabled ? 'text-gray-900 dark:text-dark-text-primary' : 'text-gray-500 dark:text-dark-text-muted'}`}>
                            {formatTriggerTime(trigger.minutes_before)}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-dark-text-muted">
                            via {getDeliveryLabel(trigger.delivery_method)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={trigger.is_enabled}
                            onChange={() => handleToggleEnabled(trigger)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                        <button
                          onClick={() => startEditing(trigger)}
                          className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                          title="Edit"
                        >
                          <Clock className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(trigger.id)}
                          className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add/Edit Form */}
              {showAddForm ? (
                <form onSubmit={handleSubmit} className="border border-gray-200 dark:border-dark-border p-4 bg-gray-50 dark:bg-dark-bg-tertiary">
                  <h3 className="font-medium text-gray-900 dark:text-dark-text-primary mb-4">
                    {editingTrigger ? 'Edit Trigger' : 'New Trigger'}
                  </h3>
                  
                  {/* Time setting */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                      Send notification
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={form.value}
                        onChange={(e) => {
                          // Allow only digits and empty string
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          setForm({ ...form, value: val });
                        }}
                        onBlur={() => {
                          // On blur, ensure minimum value of 1
                          const numValue = parseInt(form.value) || 1;
                          setForm({ ...form, value: String(Math.max(1, numValue)) });
                        }}
                        className="w-20 px-3 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-bg-secondary dark:text-dark-text-primary text-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                      <select
                        value={form.unit}
                        onChange={(e) => setForm({ ...form, unit: e.target.value as TimeUnit })}
                        className="px-3 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-bg-secondary dark:text-dark-text-primary text-sm focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="minutes">minutes</option>
                        <option value="hours">hours</option>
                        <option value="days">days</option>
                      </select>
                      <span className="text-gray-600 dark:text-dark-text-muted text-sm">before appointment</span>
                    </div>
                  </div>

                  {/* Message Type */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                      Message Type
                    </label>
                    <div className="flex gap-3">
                      {(['sms', 'email_plain', 'email_html'] as DeliveryMethod[]).map((method) => {
                        const isSelected = form.delivery_method === method;
                        const colorClass = method === 'sms' 
                          ? (isSelected ? 'border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400' : '')
                          : method === 'email_plain'
                          ? (isSelected ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' : '')
                          : (isSelected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' : '');
                        
                        return (
                          <button
                            key={method}
                            type="button"
                            onClick={() => setForm({ ...form, delivery_method: method, template_id: '' })}
                            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                              isSelected
                                ? colorClass
                                : 'border-gray-300 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-secondary text-gray-700 dark:text-dark-text-primary'
                            }`}
                          >
                            <span className={isSelected ? '' : getDeliveryColor(method)}>
                              {getDeliveryIcon(method)}
                            </span>
                            <span className="text-sm font-medium">
                              {getDeliveryLabel(method)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Template Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                      Message Template
                    </label>
                    
                    {filteredTemplates.length > 0 ? (
                      <div ref={templateDropdownRef} className="relative">
                        <button
                          type="button"
                          onClick={() => setTemplateDropdownOpen(!templateDropdownOpen)}
                          className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary text-sm text-left hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-gray-400" />
                            <span className={form.template_id ? '' : 'text-gray-500 dark:text-dark-text-muted'}>
                              {form.template_id 
                                ? templates.find(t => t.id === form.template_id)?.name 
                                : 'Select a template...'}
                            </span>
                          </div>
                          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${templateDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {templateDropdownOpen && (
                          <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden">
                            <div className="max-h-60 overflow-y-auto">
                              {filteredTemplates.map((tpl) => (
                                <button
                                  key={tpl.id}
                                  type="button"
                                  onClick={() => {
                                    setForm({ ...form, template_id: tpl.id });
                                    setTemplateDropdownOpen(false);
                                  }}
                                  className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left transition-colors ${
                                    form.template_id === tpl.id 
                                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' 
                                      : 'text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700'
                                  }`}
                                >
                                  <FileText className={`w-4 h-4 ${form.template_id === tpl.id ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
                                  <span className="flex-1">{tpl.name}</span>
                                  {form.template_id === tpl.id && <Check className="w-4 h-4" />}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-sm text-gray-500 dark:text-dark-text-muted">
                          No {getDeliveryLabel(form.delivery_method)} templates found.
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            onClose();
                            navigate(`/dashboard/templates/new?channel=${form.delivery_method}`);
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 dark:border-dark-border rounded-lg text-sm text-gray-600 dark:text-dark-text-muted hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          Create {getDeliveryLabel(form.delivery_method)} Template
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text-primary bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving || !form.template_id}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg"
                    >
                      {saving ? 'Saving...' : editingTrigger ? 'Update' : 'Add Trigger'}
                    </button>
                  </div>
                </form>
              ) : triggers.length > 0 && (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 border border-blue-200 dark:border-blue-800"
                >
                  <Plus className="w-4 h-4 mr-1.5" />
                  Add Another Trigger
                </button>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200 dark:border-dark-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text-primary bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormTriggerModal;

